fbm.add("util/io", ["util/pause", "util/screens"], function (upause, uscreens) {

    let {
        pause,
        getScreen,
        loadTemplates,
        clear
    } = {
        ...upause,
        ...uscreens
    }

    let stopped = false;

    // Command history
    let prev = getHistory();
    let historyIndex = -1;
    let tmp = "";

    function getHistory() {
        let storage = localStorage.getItem("commandHistory");
        let prev;
        if (storage) {
            try {
                let json = JSON.parse(storage);
                prev = Array.isArray(json) ? json : [];
            } catch (e) {
                prev = [];
            }
        } else {
            prev = [];
        }
        return prev;
    }

    function addToHistory(cmd) {
        prev = [cmd, ...prev];
        historyIndex = -1;
        tmp = "";

        try {
            localStorage.setItem("commandHistory", JSON.stringify(prev));
        } catch (e) {
        }
    }

    /**
     * Convert a character that needs to be typed into something that can be shown on the screen.
     * Newlines becomes <br>
     * Tabs become three spaces.
     * Spaces become &nbsp;
     * */
    function getChar(char) {
        let result;
        if (typeof char === "string") {
            if (char === "\n") {
                result = document.createElement("br");
            } else if (char === "\t") {
                let tab = document.createElement("span");
                tab.innerHTML = "&nbsp;&nbsp;&nbsp;";
                result = tab;
            } else if (char === " ") {
                let space = document.createElement("span");
                space.innerHTML = "&nbsp;";
                space.classList.add("char");
                result = space;
            } else {
                let span = document.createElement("span");
                span.classList.add("char");
                span.textContent = char;
                result = span;
            }
        }
        return result;
    }

    function link(url, options) {
        let {
            pre = "",
            text = "",
            html = "",
            target = "_blank",
            click = null,
            classes = null,
        } = options;

        let d = document.createElement("div");
        let a = document.createElement("a");
        a.setAttribute("href", url);
        a.innerText = text;
        if (html) {
            a.innerHTML = html;
        }

        if (click) {
            a.onclick = click;
            target = null;
        }
        if (classes) {
            a.classList.add(classes.split(' '));
        }
        target && a.setAttribute("target", target);

        let s = document.createElement("span");
        s.innerText = pre;
        s.appendChild(a);
        d.appendChild(s);

        return d;
    }

    function table(columns, data, drawer) {
        const t = document.createElement("table");
        t.style.width = "100%";
        const cellWidth = parseInt(100/columns, 10) + "%";
        let r = document.createElement("tr");
        for (let i = 1; i <= data.length; i++) {
            if (r === null) {
                r = document.createElement("tr");
            }
            const breakAfter = i % columns === 0 && i < data.length;
            const d = document.createElement('td');
            d.style.width = cellWidth;
            const c = drawer(data[i - 1], breakAfter);
            d.appendChild(c);
            r.appendChild(d);
            if (breakAfter) {
                t.appendChild(r);
                r = null;
            }
        }
        if (r !== null) {
            t.appendChild(r);
        }
        return t;
    }

    /**
     * Types the given text on the screen
     * @param {String|Array<String>|Node|Array<Node>} text Text to type
     * @param {Object} options Typer config
     * @param {Number} options.wait Time (ms) to wait between characters.
     * @param {Number} options.lineWait If text is an array of strings, it will wait this amount (ms) between lines
     * @param {Number} options.finalWait Time (ms) to wait when finished.
     * @param {String} options.typerClass Class to add to the typing container, in order to style is with CSS
     * @param {Boolean} options.useContainer If true, types text into the container element (3rd parameter). If false, creates a new div
     * @param {Boolean} options.stopBlinking Stop blinking when typing is done
     * @param {Boolean} options.processChars Whether to preprocess spaces, tabs and newlines to &nbsp; (3x&nbsp;) and <br>
     * @param {Boolean} options.clearContainer Clear container before typing
     * @param {Element} container DOM element where text will be typed
     */
    async function type(
        text,
        options = {},
        container = document.querySelector(".terminal")
    ) {
        if (!text) return Promise.resolve();

        let {
            wait = 10,
            initialWait = 100,
            finalWait = 100,
            lineWait = 100,
            typerClass = "",
            useContainer = false,
            stopBlinking = true,
            processChars = true,
            clearContainer = false
        } = options;

        // If text is an array, e.g. type(['foo', 'bar'])
        if (processChars && Array.isArray(text)) {
            for (const t of text)
                await type(
                    t,
                    {
                        ...options,
                        initialWait: lineWait,
                        finalWait: lineWait
                    },
                    container
                );
            return;
        }

        let interval;
        return new Promise(async (resolve) => {
            if (interval) {
                clearInterval(interval);
                interval = null;
            }

            if (stopped) {
                return;
            }

            // Create a div where all the characters can be appended to (or use the given container)
            let typer = useContainer
                ? container
                : document.createElement("div");
            typer.classList.add("typer", "active");

            if (typerClass) {
                typer.classList.add(typerClass);
            }
            // Handy if reusing the same container
            if (clearContainer) {
                container.innerHTML = "&nbsp;";
            }

            if (!useContainer) {
                container.appendChild(typer);
            }

            if (initialWait) {
                await pause(initialWait / 1000);
            }

            let queue = text;
            if (processChars) {
                queue = text.split("");
            }

            let prev;

            // Use an interval to repeatedly pop a character from the queue and type it on screen
            interval = setInterval(async () => {
                if (stopped) {
                    queue = [];
                }
                if (queue.length) {
                    let char = queue.shift();

                    // This is an optimisation for typing a large number of characters on the screen.
                    // It seems the performance degrades when trying to add 500+ DOM elements rapidly on the screen.
                    // So the content of the previous element is moved to the typer container and removed, which
                    // reduces the amount of DOM elements.
                    // This may cause issues when the element is removed while the character is still animating (red screen)
                    if (processChars && prev) {
                        prev.remove();
                        if (
                            prev.firstChild &&
                            prev.firstChild.nodeType ===
                            Node.TEXT_NODE
                        ) {
                            typer.innerText +=
                                prev.innerText;
                        } else {
                            typer.appendChild(prev);
                        }
                    }
                    let element = processChars
                        ? getChar(char)
                        : char;
                    if (element) {
                        typer.appendChild(element);

                        if (element.nodeName === "BR") {
                            scroll(container);
                        }
                    }
                    prev = element;
                } else {
                    // When the queue is empty, clean up the interval
                    clearInterval(interval);
                    await pause(finalWait / 1000);
                    if (stopBlinking) {
                        typer.classList.remove("active");
                    }
                    resolve();
                }
            }, wait);
        });
    }

    function isPrintable(keycode) {
        return (
            (keycode > 47 && keycode < 58) || // number keys
            keycode === 32 || // spacebar & return key(s) (if you want to allow carriage returns)
            (keycode > 64 && keycode < 91) || // letter keys
            (keycode > 95 && keycode < 112) || // numpad keys
            (keycode > 185 && keycode < 193) || // ;=,-./` (in order)
            (keycode > 218 && keycode < 223)
        );
    }

    function moveCaretToEnd(el) {
        var range, selection;
        if (document.createRange) {
            range = document.createRange(); //Create a range (a range is a like the selection but invisible)
            range.selectNodeContents(el); //Select the entire contents of the element with the range
            range.collapse(false); //collapse the range to the end point. false means collapse to end rather than the start
            selection = window.getSelection(); //get the selection object (allows you to change selection)
            selection.removeAllRanges(); //remove any selections already made
            selection.addRange(range); //make the range you have just created the visible selection
        }
    }

    async function promptModal(message, itype = "text", opts = {}) {

        let {
            change = null,
            after = null,
            sub = "",
            abort = true, // show abort button
        } = opts

        const d = document.createElement("div");
        d.classList.add("mint-modal");

        switch (itype) {
            case "password":
            case "number":
                break;
            default:
                itype = "text";
        }

        let abortHTML = '<a href="javascript:" class="abort col-6">&lt;-- ABORT</a>'
        if (!abort) {
            abortHTML = '<span class=" col-6">&nbsp;</span>';
        }

        d.innerHTML = `<h2>${message}</h2><div>${sub}</div>
  <input type="${itype}">
  <div class="row">
  ${abortHTML}
  <a href="javascript:" class="continue col-6">CONTINUE --&gt;</a>
  </div>
	`;

        if (opts) {
            const inputBox = d.querySelector("input");
            for (let i in opts) {
                if (i === "change" || i === "after" || i === "abort") {
                    continue;
                }
                inputBox.setAttribute(i, opts[i]);
            }
        }

        let screen = getScreen("modal-container")
        screen.appendChild(d);


        if (change !== null) {
            const f = async (e) => {
                await change(e, d, input, div);
            };
            const div = d.querySelector("div");
            const input = d.querySelector("input");
            input.addEventListener("keyup", f);
            input.addEventListener("change", f);
            input.addEventListener("blur", f);
            change(null, d, input, div);
        }
        if (after !== null) {
            await after(screen);
        }
        return new Promise(async (resolve, reject) => {
            let ip = d.querySelector("input");

            const resolver = async function () {
                let result = cleanInput(
                    ip.value
                );

                screen.remove();

                resolve(result);
            }

            ip.addEventListener("keydown", async (event) => {
                if (event.keyCode === 13) {
                    event.preventDefault();
                    return resolver();
                }
            });
            abort && d.querySelector(".abort").addEventListener("click", async () => {
                screen.remove();
                reject();
            });
            d.querySelector(".continue").addEventListener("click", async () => {
                return resolver();
            });
            ip.focus();
        });
    }

    /** Shows an input field, returns a resolved promise with the typed text on <enter> */
    async function input(pw, maxLength = null) {
        return new Promise((resolve) => {
            // This handles all user input
            const onKeyDown = (event) => {
                // ENTER
                if (event.keyCode === 13) {
                    event.preventDefault();
                    event.target.setAttribute(
                        "contenteditable",
                        false
                    );
                    let result = cleanInput(
                        event.target.textContent
                    );

                    // history
                    addToHistory(result);
                    resolve(result);
                }
                // UP
                else if (event.keyCode === 38) {
                    if (historyIndex === -1)
                        tmp = event.target.textContent;
                    historyIndex = Math.min(
                        prev.length - 1,
                        historyIndex + 1
                    );
                    let text = prev[historyIndex];
                    event.target.textContent = text;
                }
                // DOWN
                else if (event.keyCode === 40) {
                    historyIndex = Math.max(-1, historyIndex - 1);
                    let text = prev[historyIndex] || tmp;
                    event.target.textContent = text;
                }
                // BACKSPACE
                else if (event.keyCode === 8) {
                    // Prevent inserting a <br> when removing the last character
                    if (event.target.textContent.length === 1) {
                        event.preventDefault();
                        event.target.innerHTML = "";
                    }
                }
                // Check if character can be shown as output (skip if CTRL is pressed)
                else if (isPrintable(event.keyCode) && !event.ctrlKey) {
                    event.preventDefault();
                    // Wrap the character in a span
                    let span = document.createElement("span");

                    let keyCode = event.keyCode;
                    let chrCode =
                        keyCode - 48 * Math.floor(keyCode / 48);
                    let chr = String.fromCharCode(
                        96 <= keyCode ? chrCode : keyCode
                    );
                    // Add span to the input
                    span.classList.add("char");
                    span.textContent = chr;
                    event.target.appendChild(span);

                    let length = event.target.textContent.length;
                    // For password field, fill the data-pw attr with asterisks
                    // which will be shown using CSS
                    if (pw) {

                        event.target.setAttribute(
                            "data-pw",
                            Array(length).fill("*").join("")
                        );
                    }
                    moveCaretToEnd(event.target);

                    if (maxLength > 0 && length >= maxLength) {
                        event.target.setAttribute(
                            "contenteditable",
                            false
                        );
                        let result = cleanInput(
                            event.target.textContent
                        );

                        // history
                        resolve(result);
                    }
                }
            };

            // Add input to terminal
            let terminal = document.querySelector(".terminal");
            let input = document.createElement("span");
            input.setAttribute("id", "input");
            if (pw) {
                input.classList.add("password");
            }
            input.setAttribute("contenteditable", true);
            input.addEventListener("keydown", onKeyDown);
            terminal.appendChild(input);
            input.focus();
        });
    }

    async function inputBool() {
        return new Promise((resolve) => {
            // This handles all user input
            const onKeyDown = (event) => {
                // Check if character can be shown as output (skip if CTRL is pressed)
                if (isPrintable(event.keyCode) && !event.ctrlKey) {
                    event.preventDefault();
                    let keyCode = event.keyCode;
                    let chrCode =
                        keyCode - 48 * Math.floor(keyCode / 48);
                    let chr = String.fromCharCode(
                        96 <= keyCode ? chrCode : keyCode
                    );
                    if (chr === 'Y' || chr === 'N') {
                        event.target.setAttribute(
                            "contenteditable",
                            false
                        );
                        resolve(chr === 'Y');
                    }
                }
            };

            // Add input to terminal
            let terminal = document.querySelector(".terminal");
            let input = document.createElement("span");
            input.setAttribute("id", "input");
            input.setAttribute("contenteditable", true);
            input.classList.add("no-carat")
            input.addEventListener("keydown", onKeyDown);
            terminal.appendChild(input);
            input.focus();
        });
    }

// Processes the user input and executes a command
    async function parse(input) {
        input = cleanInput(input);

        if (!input) {
            return;
        }
        // Only allow words, separated by space
        let matches = String(input).match(/^(\w+)(?:\s((?:\w+(?:\s\w+)*)))?$/);

        if (!matches) {
            throw new Error("Invalid command");
        }
        let command = matches[1];
        let args = matches[2];

        let naughty = ["fuck", "shit", "die", "ass", "cunt"];
        if (naughty.some((word) => command.includes(word))) {
            throw new Error("Please don't use that language");
        }

        let module;

        // Try to import the command function
        try {
            module = fbm.load(`commands/${command}`);
        } catch (e) {
            console.error(e);
            // Kinda abusing TypeError to check if the import failed
            if (e instanceof TypeError) {
                e.message = `Unknown command: ${command}`;
            }
            // E.g. syntax error
            else {
                e.message = "Error while executing command";
            }
            throw e;
        }

        // Execute the command (default export)
        await module.default?.(args);

        return;
    }

    function cleanInput(input) {
        return input.toLowerCase().trim();
    }

    function scroll(el = document.querySelector(".terminal")) {
        el.scrollTop = el.scrollHeight;
    }

    /** Types the given text and asks input */
    async function prompt(text, pw = false) {
        await type(text);
        return input(pw);
    }

    /** Sets a global event listeners and returns when a key is hit */
    async function waitForKey(keys = [], printable = false) {
        return new Promise((resolve) => {
            const handle = (e) => {
                if (keys.length > 0 && keys.indexOf(e.key) === -1) {
                    return;
                }
                if (printable && (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey)) {
                    return;
                }
                document.removeEventListener("keyup", handle);
                document.removeEventListener("click", handle);

                resolve(e.key);
            };
            document.addEventListener("keyup", handle);
            document.addEventListener("click", handle);
        });
    }

    function enableTyping(stopIt) {
        stopped = !stopIt;
    }

    function isTypingEnabled() {
        return !stopped;
    }

    function addStylesheet(href) {
        let head = document.getElementsByTagName("HEAD")[0];

        // Create new link Element
        let link = document.createElement("link");

        // set the attributes for link element
        link.rel = "stylesheet";
        link.type = "text/css";
        link.href = href;

        // Append link element to HTML head
        head.appendChild(link);
    }

    async function clearKey(prompt = "Press any key to continue...", screen = document.querySelector(".terminal")) {
        await type(`\n${prompt}\n`, {wait: 1, lineWait: 100})
        return new Promise(async (resolve) => {
            await waitForKey([], true);
            await clear();
            resolve();
        });
    }

    return {
        prompt,
        input,
        inputBool,
        cleanInput,
        type,
        parse,
        scroll,
        waitForKey,
        enableTyping,
        isTypingEnabled,
        promptModal,
        link,
        clearKey,
        table
    };
});
